import Papa from 'papaparse'
import * as XLSX from 'xlsx'

export interface RowData {
  firstname: string
  lastname: string
  email: string
  team: string
}

export type FileType = 'csv' | 'xls' | 'xlsx'

export function detectFileType(file: File): FileType {
  const extension = file.name.split('.').pop()?.toLowerCase()
  if (extension === 'csv') return 'csv'
  if (extension === 'xls') return 'xls'
  if (extension === 'xlsx') return 'xlsx'
  throw new Error('Unsupported file type. Please upload a CSV or Excel file.')
}

export function normalizeColumnNames(
  headers: string[]
): Record<string, string> {
  const mapping: Record<string, string> = {}
  const normalizedHeaders: Record<string, string> = {
    firstname: 'firstname',
    'first name': 'firstname',
    first_name: 'firstname',
    'First Name': 'firstname',
    FIRSTNAME: 'firstname',
    lastname: 'lastname',
    'last name': 'lastname',
    last_name: 'lastname',
    'Last Name': 'lastname',
    LASTNAME: 'lastname',
    email: 'email',
    Email: 'email',
    EMAIL: 'email',
    'e-mail': 'email',
    team: 'team',
    Team: 'team',
    TEAM: 'team',
    'team name': 'team',
    team_name: 'team',
    'Team Name': 'team',
  }

  headers.forEach((header) => {
    const normalized = header.trim().toLowerCase().replace(/\s+/g, ' ')
    const mappedKey = normalizedHeaders[normalized]
    if (mappedKey) {
      mapping[header] = mappedKey
    }
  })

  return mapping
}

export async function parseCSV(file: File): Promise<RowData[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        if (results.errors.length > 0) {
          const errorMessages = results.errors
            .map((e) => `Row ${e.row ?? 0 + 1}: ${e.message}`)
            .join('; ')
          reject(new Error(`CSV parsing errors: ${errorMessages}`))
          return
        }

        const headers = results.meta.fields || []
        const columnMapping = normalizeColumnNames(headers)

        const requiredColumns = ['firstname', 'lastname', 'email', 'team']
        const missingColumns = requiredColumns.filter(
          (col) => !Object.values(columnMapping).includes(col)
        )

        if (missingColumns.length > 0) {
          reject(
            new Error(
              `Missing required columns: ${missingColumns.join(', ')}. Found columns: ${headers.join(', ')}`
            )
          )
          return
        }

        const rows: RowData[] = results.data
          .map((row: any) => {
            const mappedRow: Partial<RowData> = {}
            headers.forEach((header) => {
              const mappedKey = columnMapping[header]
              if (mappedKey && row[header] !== undefined) {
                mappedRow[mappedKey as keyof RowData] = String(
                  row[header]
                ).trim()
              }
            })

            // Skip rows where all fields are empty
            if (
              !mappedRow.firstname &&
              !mappedRow.lastname &&
              !mappedRow.email &&
              !mappedRow.team
            ) {
              return null
            }

            return {
              firstname: mappedRow.firstname || '',
              lastname: mappedRow.lastname || '',
              email: mappedRow.email || '',
              team: mappedRow.team || '',
            } as RowData
          })
          .filter((row): row is RowData => row !== null)

        resolve(rows)
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`))
      },
    })
  })
}

export async function parseExcel(file: File): Promise<RowData[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })

        const firstSheetName = workbook.SheetNames[0]
        if (!firstSheetName) {
          reject(new Error('Excel file has no sheets'))
          return
        }

        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        }) as any[][]

        if (jsonData.length === 0) {
          reject(new Error('Excel file is empty'))
          return
        }

        const headers = jsonData[0].map((h: any) => String(h).trim())
        const columnMapping = normalizeColumnNames(headers)

        const requiredColumns = ['firstname', 'lastname', 'email', 'team']
        const missingColumns = requiredColumns.filter(
          (col) => !Object.values(columnMapping).includes(col)
        )

        if (missingColumns.length > 0) {
          reject(
            new Error(
              `Missing required columns: ${missingColumns.join(', ')}. Found columns: ${headers.join(', ')}`
            )
          )
          return
        }

        const rows: RowData[] = jsonData
          .slice(1) // Skip header row
          .map((row: any[]) => {
            const mappedRow: Partial<RowData> = {}
            headers.forEach((header, colIndex) => {
              const mappedKey = columnMapping[header]
              if (mappedKey && row[colIndex] !== undefined) {
                mappedRow[mappedKey as keyof RowData] = String(
                  row[colIndex]
                ).trim()
              }
            })

            if (
              !mappedRow.firstname &&
              !mappedRow.lastname &&
              !mappedRow.email &&
              !mappedRow.team
            ) {
              return null
            }

            return {
              firstname: mappedRow.firstname || '',
              lastname: mappedRow.lastname || '',
              email: mappedRow.email || '',
              team: mappedRow.team || '',
            } as RowData
          })
          .filter((row): row is RowData => row !== null)

        resolve(rows)
      } catch (error) {
        reject(
          new Error(
            `Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`
          )
        )
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'))
    }

    reader.readAsArrayBuffer(file)
  })
}

export async function parseFile(file: File): Promise<RowData[]> {
  const fileType = detectFileType(file)

  if (fileType === 'csv') {
    return parseCSV(file)
  } else {
    return parseExcel(file)
  }
}
